import { OptimizelyProvider } from '@optimizely/react-sdk'
import React from 'react'

import { Alert, Loader } from 'packages/common'
import { createOptimizelyInstance, decodeUserId } from 'packages/optimizely'

import { Slugs, useI18n } from 'app/fieldapp/i18n'
import { User } from 'app/fieldapp/store/users'

import FieldAppRoot from '../FieldAppRoot'
import { isViewingSharedClean } from '../schedule/schedule.utils'

const optimizelyKey = process.env.REACT_APP_OPTIMIZELY_SDK_KEY || ''
const optimizely = createOptimizelyInstance(optimizelyKey)

const AuthErrorAlert = () => {
  const { t } = useI18n()

  return (
    <Alert alertType={'danger'}>
      <div>{t(Slugs.authErrorBody)}</div>
    </Alert>
  )
}

export type FieldAppIndexProps = {
  fetchCurrentUser: () => Promise<void>
  getActiveUser: () => User | undefined
  getAuthError: () => Error | undefined
  getAuthToken: () => string | undefined
  getAuthUser: () => User | undefined
  initialUrl: string
}

export type FieldAppIndexState = {
  hasAuthError: boolean
  initializing: boolean
}

/**
 * A top-level app wrapper to handle bootstrapping the app before anything is rendered.
 * This currently includes:
 * - Fetching the current user data, and ensuring they are authenticated
 */
export const FieldAppIndex: React.FC<FieldAppIndexProps> = ({
  fetchCurrentUser,
  getActiveUser,
  getAuthError,
  getAuthToken,
  getAuthUser,
  initialUrl,
}) => {
  const authError = getAuthError()
  const authUser = getAuthUser()
  const activeUser = getActiveUser()
  const token = getAuthToken()
  const monolithUserId = activeUser?.monolithUserId || decodeUserId(token)
  const isAdmin = Boolean(authUser?.isSuperuser)
  const isSharedClean = isViewingSharedClean()

  const [state, setState] = React.useState<FieldAppIndexState>({
    hasAuthError: false,
    initializing: true,
  })
  const { hasAuthError, initializing } = state

  /*
   * Initialization hook
   * Once the app-level auth initialization has completed, we can init here.
   * If there is an auth error, simply show the "not authorized" error;
   * otherwise, fetch the authenticated and all other necessary data.
   */
  React.useEffect(() => {
    const initialize = async () => {
      if (authError) {
        setState({ hasAuthError: true, initializing: false })
      } else {
        await fetchCurrentUser()
      }
    }

    if (!isSharedClean && initializing) {
      initialize()
    }
  }, [authError, fetchCurrentUser, initializing, isSharedClean])

  /*
   * Current User watcher
   * Once we successfully fetch the current user, we can disable the init flag
   * and finally hide the loader.
   */
  React.useEffect(() => {
    if (initializing && (activeUser || isSharedClean)) {
      setState({ hasAuthError: false, initializing: false })
    }
  }, [activeUser, initializing, isSharedClean])

  if (initializing) {
    return <Loader />
  }

  if (hasAuthError) {
    return <AuthErrorAlert />
  }

  return (
    <OptimizelyProvider
      optimizely={optimizely}
      user={{
        attributes: {
          app: 'field-app',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          employee_user_id: Number(monolithUserId),
        },
        id: `${monolithUserId}`,
      }}
    >
      <FieldAppRoot initialUrl={initialUrl} isAdmin={isAdmin} />
    </OptimizelyProvider>
  )
}
